// ---------------------------------------------------------------------------
// Qual cache local o Firestore usa — e por que a resposta depende da PLATAFORMA.
//
// ## O que estava aqui, e por que deixou de servir
//
// Desde 15/04/2026 (`7dd4cc9`) o app inteiro inicializava com
// `persistentLocalCache({ tabManager: persistentMultipleTabManager() })`.
// Era a escolha CERTA — para a web, onde a pessoa abre `uselivra.com` em
// várias abas e elas precisam coordenar quem fala com o servidor.
//
// O app nativo nasceu em 12/08, quatro meses depois, e ninguém revisitou a
// linha. **Um WKWebView/WebView do Capacitor tem UMA aba, por construção** —
// não existe segunda aba para coordenar. É a mesma classe de "premissa correta
// que deixou de ser verdadeira" que já custou o `MainActivity` (BUG-155), o
// `resolveAuthDomain` (BUG-114) e o `resolveInstallPlatform`.
//
// ## Por que isso não é só desperdício — é o travamento
//
// Medido no `firebase@12.10.0` instalado (`common-*.node.cjs.js`), o modo
// multi-aba liga o `IndexedDbPersistence` com eleição de líder ("primary
// lease"), e para mantê-la ele:
//
//  · registra `visibilitychange` → enfileira `updateClientMetadataAndTryBecome
//    Primary()`, ou seja **uma transação de IndexedDB toda vez que o app sai
//    ou volta do background**;
//  · registra `pagehide` → tenta liberar o lease, com o comentário literal do
//    SDK: *"Attempt graceful shutdown (including releasing our primary lease),
//    but there's no guarantee it will complete"*;
//  · mantém um `clientMetadataRefresher` gravando periodicamente no IndexedDB.
//
// O iOS **não dispara `pagehide`** ao mandar o app pro background, então o
// lease não é liberado — fica preso pela sessão anterior da própria pessoa. E
// o WebKit invalida transações de IndexedDB que atravessam a suspensão (bug
// 226547 do WebKit, que o PRÓPRIO SDK contorna — ver abaixo).
//
// A `AsyncQueue` do Firestore é estritamente serial
// (`this.tail = this.tail.then(...)`): uma operação que nunca resolve trava a
// cadeia inteira para sempre, e uma que rejeita grava `this.failure` e faz
// **toda chamada seguinte curto-circuitar numa promise rejeitada**. Os dois
// desfechos dão o mesmo sintoma medido em device: a tela continua com o que já
// tinha carregado e **nenhum dado novo chega até o app ser morto e reaberto**.
//
// ## O detalhe que fecha o caso
//
// O SDK carrega um workaround explícito para esse bug do WebKit:
//
//     const safariIndexdbBugVersionRegex = /(?:Version|Mobile)\/1[456]/;
//     if (util.isSafari() && (navigator.appVersion.match(...) || ...)) {
//       this.queue.enterRestrictedMode(/* purgeExistingTasks= */ true);
//     }
//
// e `isSafari()` (`@firebase/util`) exige `userAgent.includes('Safari')`. O UA
// do WKWebView do Capacitor **não contém "Safari"** (e medimos que o projeto
// não customiza o UA — nenhum `appendUserAgent`/`overrideUserAgent` no
// `capacitor.config.json`). Ou seja: o app das lojas roda no MESMO motor, com
// o MESMO bug, e é excluído do remendo do próprio SDK por uma string de user
// agent. No mesmo arquivo existe `isSafariOrWebkit()`, que pegaria o WKWebView;
// o Firestore usa o outro.
//
// ## A decisão
//
//  · **nativo → `single-owner`**: `persistentSingleTabManager({ forceOwnership:
//    true })`. Acaba a eleição, acaba o refresher periódico, e `forceOwnership`
//    faz o cliente TOMAR o lease sempre — sem esperar a liberação de uma sessão
//    anterior que nunca liberou. Zero perda de função onde só existe uma aba.
//  · **web → `multi-tab`**: byte a byte o comportamento de hoje. `forceOwnership`
//    com duas abas abertas é comportamento **indefinido** pela própria doc do
//    Firebase, e `uselivra.com` é um site: a pessoa PODE abrir duas abas.
//    Mudar a web para consertar um sintoma que só foi medido no app seria
//    trocar um bug conhecido por um desconhecido.
//
// ⚠️ **Consequência assumida e registrada:** a PWA instalada no iOS roda no
// mesmo WebKit e continua no modo multi-aba, ou seja segue exposta. Ela não é
// `isNativeApp()` (BUG-64) e, sendo servida de um domínio, herda o risco das
// duas abas. Se o travamento aparecer na PWA, a saída é `single-owner` lá
// também — e aí o custo de duas abas passa a ser decisão do fundador.
// ---------------------------------------------------------------------------

/**
 * Como o cliente disputa a posse do cache local (IndexedDB).
 *
 *  · `single-owner` — dono único e forçado; sem eleição de líder.
 *  · `multi-tab`    — coordenação entre abas do mesmo site.
 */
export type FirestoreCacheMode = "single-owner" | "multi-tab";

/**
 * Decisão pura, para o teste conseguir alcançá-la sem inicializar o Firebase.
 * O `config.ts` só traduz o resultado no `tabManager` correspondente.
 */
export function resolveFirestoreCacheMode(native: boolean): FirestoreCacheMode {
  return native ? "single-owner" : "multi-tab";
}
